/**
 * one-stop/theme — the tenant tint and the One-Stop stylesheet (JAI-1903).
 *
 * THE STYLESHEET IS A FILE. Every rule lives in `./onestop.css` inside `@layer jrni`,
 * imported statically. Nothing is injected from an effect any more, so the One-Stop
 * output carries no `<style>` element and a strict CSP needs no 'unsafe-inline'.
 *
 * THE TINT IS DECLARED ON :root (renderer obligation `tokens-on-root`). A template's
 * `@layer site { :root { --jrni-color-primary-1: … } }` then beats it by layer order,
 * without !important. The tint is a CONSTRUCTABLE stylesheet adopted by the document —
 * neither a `<style>` element nor a `style=` attribute — and it is removed on unmount.
 *
 * ONE EXCEPTION, THE STUDIO PREVIEW. The same renderer mounts inside Studio for the
 * Journey Builder, where a :root tint would re-tint Studio's own chrome. `scope="preview"`
 * declares the same values on the One-Stop root instead. No template renders in that
 * mode (a custom-html preview renders in its own document), so the obligation is not
 * weakened where it applies.
 *
 * ONE-STOP HOST DEFAULTS. Beside the brand ramp the sheet carries the one token default
 * the One-Stop look has always used and Studio does not: the flat card shadow
 * (`--jrni-shadow-card`). Declared here, not in the static file, for the same Studio reason.
 */
import { useEffect } from "react";
import type { JSX } from "react";
import "./onestop.css";

/** The brand steps a tenant may re-tint. Everything else derives. */
export interface OneStopTint {
  readonly primary1?: string;
  readonly primary2?: string;
  readonly primary3?: string;
  readonly primary4?: string;
  readonly primaryText?: string;
}

/** Where the tint is declared: the document root (One-Stop host) or the One-Stop root (Studio preview). */
export type OneStopThemeScope = "document" | "preview";

/* ONE DEFAULT ACROSS EVERY CUSTOMER SURFACE (KP, 2026-09-17): the platform purple, with
 * the same derivation `onestop-tab.tsx#tintFor` applies to a served brand. */
const DEFAULT_BRAND = "#5142C7";
const DEFAULT_TINT: Required<OneStopTint> = {
  primary1: DEFAULT_BRAND,
  primary2: `color-mix(in srgb, ${DEFAULT_BRAND} 26%, #fff)`,
  primary3: `color-mix(in srgb, ${DEFAULT_BRAND} 13%, #fff)`,
  primary4: `color-mix(in srgb, ${DEFAULT_BRAND} 6%, #fff)`,
  primaryText: `color-mix(in srgb, ${DEFAULT_BRAND} 82%, #000)`,
};

/** The class the preview scope declares on; `OneStopRoot` renders it. */
export const ONE_STOP_ROOT_CLASS = "osr-root";

export function tintCss(tint: OneStopTint, scope: OneStopThemeScope): string {
  const v = { ...DEFAULT_TINT, ...tint };
  const selector = scope === "document" ? ":root" : `.${ONE_STOP_ROOT_CLASS}`;
  return `@layer jrni {
${selector} {
  --jrni-color-primary-1: ${v.primary1};
  --jrni-color-primary-2: ${v.primary2};
  --jrni-color-primary-3: ${v.primary3};
  --jrni-color-primary-4: ${v.primary4};
  --jrni-color-brand-fill: ${v.primary1};
  --jrni-color-primary-text: ${v.primaryText};
  --jrni-color-utility-focus: ${v.primary1};
  --jrni-shadow-card: var(--jrni-shadow-chrome);
}
}`;
}

/**
 * The accent ramp for directory nodes. ui-config: the design system's nine tertiary
 * accents, the public ramp the contract pairs with an `-ink` token each. The prototype's
 * four literal tenant extensions are gone (renderer obligation `public-tokens-only`); a
 * node id maps onto the nine deterministically, so the same department keeps its colour.
 */
const DEPT_RAMP = [
  "var(--jrni-color-tertiary-dark-blue)",
  "var(--jrni-color-tertiary-red)",
  "var(--jrni-color-tertiary-purple)",
  "var(--jrni-color-tertiary-light-blue)",
  "var(--jrni-color-tertiary-turquoise)",
  "var(--jrni-color-tertiary-yellow)",
  "var(--jrni-color-tertiary-green)",
  "var(--jrni-color-tertiary-orange)",
  "var(--jrni-color-tertiary-pink)",
] as const;

export function accentFor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return DEPT_RAMP[hash % DEPT_RAMP.length]!;
}

/* ── the tint sheet ─────────────────────────────────────────────── */

const sheets = new Map<OneStopThemeScope, { sheet: CSSStyleSheet | HTMLStyleElement; users: number }>();

function supportsAdopted(doc: Document): boolean {
  return typeof CSSStyleSheet !== "undefined" && "replaceSync" in CSSStyleSheet.prototype && "adoptedStyleSheets" in doc;
}

function attach(doc: Document, scope: OneStopThemeScope, css: string): () => void {
  let entry = sheets.get(scope);
  if (!entry) {
    // Constructable sheet first; a <style> element only for engines without one
    // (Safari < 16.4). Either holds the tint only; the rules are the static file.
    const sheet = supportsAdopted(doc) ? new CSSStyleSheet() : doc.createElement("style");
    entry = { sheet, users: 0 };
    sheets.set(scope, entry);
  }
  const { sheet } = entry;
  if (sheet instanceof HTMLStyleElement) {
    if (sheet.textContent !== css) sheet.textContent = css;
    if (!sheet.isConnected) doc.head.appendChild(sheet);
  } else {
    sheet.replaceSync(css);
    if (!doc.adoptedStyleSheets.includes(sheet)) doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
  }
  entry.users += 1;
  return () => {
    const current = sheets.get(scope);
    if (!current) return;
    current.users -= 1;
    if (current.users > 0) return;
    if (current.sheet instanceof HTMLStyleElement) current.sheet.remove();
    else doc.adoptedStyleSheets = doc.adoptedStyleSheets.filter((s) => s !== current.sheet);
    sheets.delete(scope);
  };
}

/**
 * Declares the tint. Also sets `data-density="comfortable"` on `<body>`: the design
 * system's layout reset targets `body:not([data-density]) main > section` with
 * `!important` padding. The previous value is restored on unmount.
 */
export function OneStopTheme({ tint = {}, scope = "document" }: { tint?: OneStopTint; scope?: OneStopThemeScope }): JSX.Element | null {
  const css = tintCss(tint, scope);
  useEffect(() => {
    const doc = typeof document === "undefined" ? null : document;
    if (!doc) return;
    const detach = attach(doc, scope, css);
    const previousDensity = doc.body.getAttribute("data-density");
    doc.body.setAttribute("data-density", "comfortable");
    return () => {
      detach();
      if (previousDensity === null) doc.body.removeAttribute("data-density");
      else doc.body.setAttribute("data-density", previousDensity);
    };
  }, [css, scope]);
  return null;
}
